import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Routes, Route } from "react-router";
import { App, RequirePolicy, useUser, useConfig, createEmbed } from "@livefree/applications";
import { ReactiveCore, DexieDatabaseProvider, useReactor, useSuccessReactor } from "@livefree/reactive";
import { MqlRequestBuilder, dexieRequestBrokerAddon } from "@livefree/request-broker";
import { RestClient, ApiError } from "@livefree/js-sdk";
import { EnvironmentConfigurations, useComponent } from "@livefree/runtime";
import { Breadcrumbs, BreadcrumbsItem, Card, Skeleton, Button, Dropdown, Label, ComboBox, Input, FieldError, ListBox, Table, ScrollShadow } from "@heroui/react";
import { useState, useEffect, createElement } from "react";
import { Widget } from "@heroui-pro/react";
import { DateTime, parseAndFormatDateTime, formatCurrencyUSD, parseAndFormatDate, toISO } from "@livefree/common";
import { Pill, WidgetField, SaveButton, DrawerActions, withDrawerController, useDrawerController, Forbidden } from "@livefree/react-ui";
import { Uniformly, FormField, SelectField, DateField, TextField, masks, CheckboxField } from "@livefree/uniformly";
const reactive = new ReactiveCore("subscriptions");
const ScheduleType = {
  Monthly: 1,
  Quarterly: 2,
  Annual: 3,
  Semiannual: 4,
  TenMonths: 5,
  Triennial: 6,
  EighteenMonth: 8
};
const SubscriptionStatusType = {
  Active: 1,
  OnHold: 2,
  Canceled: 3,
  PendingCancel: 4
};
class SubscriptionsClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "subscriptions");
  }
  /**
   * `GET /subscriptions?accountId=` — the account's subscription.
   *
   * `null` rather than a rejection when the account has none — the route's 404 is an ordinary
   * answer here, not a failure, so no error handler mistakes an empty account for one.
   */
  async findByAccount(accountId) {
    try {
      return await this.get({ accountId: String(accountId) });
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 404) return null;
      throw error;
    }
  }
  /** `GET /subscriptions/{id}` — a single subscription by id. */
  async getById(id) {
    return await this.fetch(id);
  }
  /**
   * `PUT /subscriptions/{id}` — replace a subscription.
   *
   * The API replaces rather than patches, so `model` must be the whole subscription. Read it with
   * {@link getById} first and spread your changes over it.
   */
  async updateSubscription(id, model) {
    return await this.update(id, model);
  }
  /**
   * `POST /subscriptions/query` — run an MQL query over subscriptions and get back a page of
   * results (`values` plus `totalCount`/`skip`/`take`).
   *
   * Pass the request-broker `metadata` (the filters/sorting/paging the frontend collected); it's
   * compiled to an MQL string via {@link MqlRequestBuilder} before invoking the API. With no
   * metadata, all visible subscriptions are fetched. Named `queryMql` because `query` is reserved
   * on `BaseEndpoint`.
   *
   * Sent as `text/plain`: the route binds a `PlainTextBody`, and the SDK's serializer only
   * JSON-encodes when the content type is `application/json` — the default would post a
   * JSON-quoted string the API cannot bind.
   */
  async queryMql(metadata) {
    const query = metadata ? MqlRequestBuilder.create(metadata).build("SubscriptionModel") : "FETCH SubscriptionModel;";
    return await this.httpPOST({
      url: this.appendUrl("/query"),
      body: query,
      options: { contentType: "text/plain" }
    });
  }
  /** `GET /subscriptions/{id}/adjustments` — the subscription's adjustments, read-only. */
  async getAdjustments(id) {
    return await this.httpGET({
      url: this.appendUrl(`/${id}/adjustments`)
    });
  }
  /**
   * `GET /subscriptions/{id}/items` — the line items on the subscription's originating order,
   * read-only.
   */
  async getItems(id) {
    return await this.httpGET({
      url: this.appendUrl(`/${id}/items`)
    });
  }
  /**
   * `POST /subscriptions/{id}/status` — move the subscription to another status under the
   * transition rules; resolves to the saved subscription. A replace never changes status.
   */
  async changeStatus(id, request) {
    return await this.httpPOST({
      url: this.appendUrl(`/${id}/status`),
      body: request
    });
  }
  /** `GET /subscriptions/{id}/status-history` — every status the subscription has held, newest first. */
  async getStatusHistory(id) {
    return await this.httpGET({
      url: this.appendUrl(`/${id}/status-history`)
    });
  }
  /** `GET /subscriptions/status-reasons` — every status reason, for the status form. */
  async getStatusReasons() {
    return await this.httpGET({
      url: this.appendUrl("/status-reasons")
    });
  }
  /**
   * `GET /subscriptions/{id}/scheduled-status-change` — the hold's scheduled end, or `null` when
   * nothing is scheduled (the route's 404).
   */
  async findScheduledStatusChange(id) {
    try {
      return await this.httpGET({
        url: this.appendUrl(`/${id}/scheduled-status-change`)
      });
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 404) return null;
      throw error;
    }
  }
}
class SubscriptionUtilitiesClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "subscriptions/utilities");
  }
  /** `POST /subscriptions/utilities/create` — manually create a subscription from a paid order. */
  async createSubscription(request) {
    return await this.httpPOST({
      url: this.appendUrl("/create"),
      body: request
    });
  }
  /**
   * `POST /subscriptions/utilities/reset` — reset a subscription's next renewal, schedule and
   * amount.
   */
  async resetSubscription(request) {
    return await this.httpPOST({
      url: this.appendUrl("/reset"),
      body: request
    });
  }
  /**
   * `GET /subscriptions/utilities/eligible-orders?accountId=` — the account's orders eligible to
   * back a manually created subscription.
   */
  async getEligibleOrders(accountId) {
    return await this.httpGET({
      url: this.appendUrl("/eligible-orders"),
      query: { accountId: String(accountId) }
    });
  }
}
const SubscriptionsConfig = EnvironmentConfigurations.hydrate({
  development: {
    host: "https://localhost:9002",
    api: {
      host: "https://localhost:7104",
      apiKey: "SubscriptionsAPI"
    }
  },
  staging: {
    host: "https://lf-artemis-staging.azurewebsites.net",
    api: {
      host: "https://lf-artemis-api-staging.azurewebsites.net",
      apiKey: "SubscriptionsAPI"
    }
  },
  production: {
    host: [
      "https://artemis.lifebeacon.com",
      "https://app.alert1.com",
      "https://app.lifebeacon.com"
    ],
    api: {
      host: "https://artemis.lfapi.net",
      apiKey: "SubscriptionsAPI"
    }
  }
});
reactive.service(
  "subscriptions",
  SubscriptionsClient
)((Service) => new Service(SubscriptionsConfig.current.api.host));
const SubscriptionUtilitiesService = reactive.service(
  "utilities",
  SubscriptionUtilitiesClient
)((Service) => new Service(SubscriptionsConfig.current.api.host));
SubscriptionUtilitiesService.reactor("utilitiesReactor", {
  error: (response) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const storeFormat = (store) => Object.entries(store).reduce((result, [key, value]) => {
  result[key] = value.join(",");
  return result;
}, {});
const db = new DexieDatabaseProvider([dexieRequestBrokerAddon]);
const ReactiveConfig = {
  db: {
    name: "livefree.subscriptions.db",
    provider: db,
    init: (db2) => {
      db2.version(1).stores(storeFormat(_store));
    }
  }
};
const _store = {
  subscriptions: ["&id"]
};
const render = reactive.configure(ReactiveConfig).render;
const Shell = ({ children }) => /* @__PURE__ */ jsx("div", { className: "p-4 h-full flex flex-col", children });
let Bootstrapped;
const AppLayout = render(({ children }) => {
  Bootstrapped ??= App.bootstrap(Shell, { runtime: appRuntime });
  return /* @__PURE__ */ jsx(Bootstrapped, { children });
});
const SubscriptionsView = () => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4 w-full h-full", children: [
  /* @__PURE__ */ jsx("div", { className: "flex justify-between items-center", children: /* @__PURE__ */ jsxs(Breadcrumbs, { children: [
    /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Subscriptions" }),
    /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Subscriptions" })
  ] }) }),
  /* @__PURE__ */ jsx(Card, { className: "flex-1", children: /* @__PURE__ */ jsx(Card.Content, { className: "p-3", children: /* @__PURE__ */ jsx("p", { className: "text-muted", children: "Subscriptions app scaffold." }) }) })
] });
const SubscriptionsRoutes = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsx(Route, { path: "/*", element: /* @__PURE__ */ jsx(SubscriptionsView, {}) }) });
const SUBSCRIPTION_STATUS_LABELS = Object.freeze({
  [SubscriptionStatusType.Active]: "Active",
  [SubscriptionStatusType.OnHold]: "On Hold",
  [SubscriptionStatusType.Canceled]: "Canceled",
  [SubscriptionStatusType.PendingCancel]: "Cancellation Pending"
});
const STATUS_TONES = Object.freeze({
  [SubscriptionStatusType.Active]: "success",
  [SubscriptionStatusType.OnHold]: "warn",
  [SubscriptionStatusType.PendingCancel]: "warn",
  [SubscriptionStatusType.Canceled]: "danger"
});
const statusTone = (status) => STATUS_TONES[status] ?? "neutral";
const TRANSITIONS = Object.freeze({
  [SubscriptionStatusType.Active]: [SubscriptionStatusType.OnHold, SubscriptionStatusType.Canceled],
  [SubscriptionStatusType.OnHold]: [SubscriptionStatusType.Active, SubscriptionStatusType.Canceled],
  [SubscriptionStatusType.PendingCancel]: [
    SubscriptionStatusType.Active,
    SubscriptionStatusType.Canceled
  ],
  [SubscriptionStatusType.Canceled]: [SubscriptionStatusType.Active]
});
const statusChoices = (status) => (TRANSITIONS[status] ?? []).map((id) => ({ id, label: SUBSCRIPTION_STATUS_LABELS[id] }));
const needsReason = (status) => status != null && status !== SubscriptionStatusType.Active;
const reasonsFor = (reasons, status) => (reasons ?? []).filter((r) => r.status === status);
const toStatusRequest = (data, reasons) => {
  const status = data.status;
  const request = { status };
  if (!needsReason(status)) return request;
  const reason = reasonsFor(reasons, status).find((r) => r.id === data.reasonId);
  request.reasonId = data.reasonId;
  if (reason?.allowFreeText) request.reason = (data.reason ?? "").trim();
  if (status === SubscriptionStatusType.OnHold && data.holdUntil)
    request.holdUntil = DateTime.fromISO(data.holdUntil, { zone: "utc" }).toLocal().toISODate();
  if (status === SubscriptionStatusType.Canceled)
    request.devicesReturned = Boolean(data.devicesReturned);
  return request;
};
const holdLastDay = (change) => change?.revertOnUtc ? DateTime.fromISO(change.revertOnUtc, { zone: "utc" }).setZone("America/Denver").minus({ minutes: 1 }).toFormat("MM/dd/yyyy") : null;
const STATUS_HISTORY_PREVIEW = 5;
const HistoryEntry = ({ entry }) => /* @__PURE__ */ jsxs("li", { className: "flex flex-col gap-0.5 border-b border-[var(--border-subtle)] py-2 last:border-b-0", children: [
  /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(Pill, { tone: statusTone(entry.status), children: entry.statusName }),
    entry.reason && /* @__PURE__ */ jsx("span", { className: "min-w-0 truncate text-sm", children: entry.reason })
  ] }),
  /* @__PURE__ */ jsxs("span", { className: "text-muted text-xs", children: [
    parseAndFormatDateTime(entry.changedOnUtc, "utc"),
    entry.changedBy ? ` · ${entry.changedBy}` : ""
  ] })
] });
const SubscriptionStatusHistory = ({ history, isLoading }) => {
  const [showAll, setShowAll] = useState(false);
  if (isLoading && !history)
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", "aria-busy": "true", children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "h-5 w-full rounded-lg" }),
      /* @__PURE__ */ jsx(Skeleton, { className: "h-5 w-full rounded-lg" })
    ] });
  if (!history?.length) return /* @__PURE__ */ jsx("p", { className: "text-muted text-sm", children: "No status changes recorded." });
  const hasMore = history.length > STATUS_HISTORY_PREVIEW;
  const shown = showAll ? history : history.slice(0, STATUS_HISTORY_PREVIEW);
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
    /* @__PURE__ */ jsx(
      "ul",
      {
        "aria-label": "Status history",
        className: showAll ? "max-h-72 overflow-y-auto pr-1" : void 0,
        children: shown.map((entry) => /* @__PURE__ */ jsx(HistoryEntry, { entry }, `${entry.changedOnUtc}-${entry.status}`))
      }
    ),
    hasMore && /* @__PURE__ */ jsx(
      Button,
      {
        size: "sm",
        variant: "tertiary",
        className: "self-start",
        onPress: () => setShowAll((value) => !value),
        children: showAll ? "Show recent" : `Show all (${history.length})`
      }
    )
  ] });
};
const formatUtcDate = (value) => value ? parseAndFormatDate(value, "utc", { tz: "utc" }) : null;
const SectionHeading = ({ id, children }) => /* @__PURE__ */ jsx("h3", { id, className: "text-muted mb-2 text-xs font-medium tracking-wide uppercase", children });
const SubscriptionWidgetActions = Object.freeze({
  ChangeStatus: "change-status",
  Reset: "reset"
});
const ACTION_ITEMS = [
  {
    key: SubscriptionWidgetActions.ChangeStatus,
    title: "Change status",
    icon: "ri-exchange-line"
  },
  {
    key: SubscriptionWidgetActions.Reset,
    title: "Reset subscription",
    icon: "ri-arrow-left-right-fill"
  }
];
const SubscriptionWidget$1 = ({
  subscription,
  isLoading,
  isMissing,
  isSaving,
  scheduledChange,
  statusHistory,
  isHistoryLoading,
  actions = [],
  onAction,
  onCreate,
  onSavePaymentProfile
}) => {
  const PaymentProfileField = useComponent("accounts.payment-profiles-field");
  const status = subscription?.status;
  const [form, setForm] = useState({ isValid: false, data: {} });
  useEffect(() => setForm({ isValid: false, data: {} }), [subscription]);
  const chosenProfileId = form.data.paymentProfileId;
  const hasNewProfile = chosenProfileId != null && Number(chosenProfileId) !== Number(subscription?.paymentProfileId);
  const menuItems = ACTION_ITEMS.filter((item) => actions.includes(item.key));
  return /* @__PURE__ */ jsxs(Widget, { className: "w-full", "aria-busy": isLoading || void 0, children: [
    /* @__PURE__ */ jsxs(Widget.Header, { children: [
      /* @__PURE__ */ jsx(Widget.Title, { children: "Subscription" }),
      subscription && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(Pill, { tone: statusTone(status), children: subscription.statusName ?? status }),
        menuItems.length > 0 && /* @__PURE__ */ jsxs(Dropdown, { isDisabled: isLoading, children: [
          /* @__PURE__ */ jsx(Button, { isIconOnly: true, size: "sm", variant: "tertiary", "aria-label": "Subscription actions", children: /* @__PURE__ */ jsx("i", { className: "ri-more-2-line" }) }),
          /* @__PURE__ */ jsx(Dropdown.Popover, { children: /* @__PURE__ */ jsx(Dropdown.Menu, { items: menuItems, onAction: (key) => onAction?.(key), children: (item) => /* @__PURE__ */ jsxs(Dropdown.Item, { id: item.key, textValue: item.title, children: [
            /* @__PURE__ */ jsx("i", { className: `${item.icon} mr-2` }),
            /* @__PURE__ */ jsx(Label, { children: item.title })
          ] }, item.key) }) })
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx(Widget.Content, { children: isMissing ? /* @__PURE__ */ jsxs("div", { className: "flex min-h-32 flex-col items-center justify-center gap-3 text-center", children: [
      /* @__PURE__ */ jsx("p", { className: "text-muted text-sm", children: "No subscription on this account." }),
      onCreate && /* @__PURE__ */ jsxs(Button, { variant: "primary", onPress: onCreate, children: [
        /* @__PURE__ */ jsx("i", { className: "ri-add-line" }),
        "Create subscription"
      ] })
    ] }) : /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-2", children: [
      /* @__PURE__ */ jsxs("section", { "aria-labelledby": "subscription-details-heading", className: "min-w-0", children: [
        /* @__PURE__ */ jsx(SectionHeading, { id: "subscription-details-heading", children: "Details" }),
        /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2", children: [
          /* @__PURE__ */ jsx(WidgetField, { label: "Amount", isLoading, children: subscription && `${formatCurrencyUSD(subscription.amount)} / ${(subscription.scheduleName ?? "").toLowerCase()}` }),
          /* @__PURE__ */ jsx(WidgetField, { label: "Next renewal", isLoading, children: formatUtcDate(subscription?.nextRenewalDateUtc) }),
          /* @__PURE__ */ jsx(WidgetField, { label: "Billing day", isLoading, children: subscription?.adjustedBillingDay ?? subscription?.billingDay }),
          /* @__PURE__ */ jsx(WidgetField, { label: "Started", isLoading, children: formatUtcDate(subscription?.startsOn) }),
          subscription?.termExpiresOnUtc && /* @__PURE__ */ jsx(WidgetField, { label: "Term expires", isLoading, children: formatUtcDate(subscription.termExpiresOnUtc) }),
          holdLastDay(scheduledChange) && /* @__PURE__ */ jsx(WidgetField, { label: "On hold until", isLoading, children: holdLastDay(scheduledChange) }),
          subscription?.statusReason && /* @__PURE__ */ jsx(WidgetField, { label: "Status reason", isLoading, children: subscription.statusReason })
        ] }),
        subscription && /* @__PURE__ */ jsx(RequirePolicy, { resource: "Subscriptions", operation: "Update", children: /* @__PURE__ */ jsxs(
          Uniformly,
          {
            className: "mt-3 flex flex-col items-stretch gap-2 sm:flex-row sm:items-end",
            onChange: setForm,
            children: [
              /* @__PURE__ */ jsx("div", { className: "w-full min-w-0 sm:flex-1", children: /* @__PURE__ */ jsx(
                PaymentProfileField,
                {
                  name: "paymentProfileId",
                  label: "Payment profile",
                  accountId: subscription.accountId,
                  value: subscription.paymentProfileId,
                  size: "sm"
                }
              ) }),
              /* @__PURE__ */ jsx(
                SaveButton,
                {
                  className: "w-full sm:w-auto",
                  size: "sm",
                  isDisabled: !hasNewProfile,
                  isLoading: isSaving,
                  onPress: () => onSavePaymentProfile?.(Number(chosenProfileId))
                }
              )
            ]
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs(
        "section",
        {
          "aria-labelledby": "subscription-history-heading",
          className: "min-w-0 border-t border-[var(--border-subtle)] pt-4 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-6",
          children: [
            /* @__PURE__ */ jsx(SectionHeading, { id: "subscription-history-heading", children: "Status history" }),
            /* @__PURE__ */ jsx(SubscriptionStatusHistory, { history: statusHistory, isLoading: isHistoryLoading })
          ]
        }
      )
    ] }) })
  ] });
};
const SUBSCRIPTION_SCHEDULES = Object.freeze([
  { id: ScheduleType.Monthly, label: "Monthly", monthsPerPeriod: 1 },
  { id: ScheduleType.Quarterly, label: "Quarterly", monthsPerPeriod: 3 },
  { id: ScheduleType.Semiannual, label: "Semiannual", monthsPerPeriod: 6 },
  { id: ScheduleType.TenMonths, label: "Ten Months", monthsPerPeriod: 10 },
  { id: ScheduleType.Annual, label: "Annual", monthsPerPeriod: 12 },
  { id: ScheduleType.EighteenMonth, label: "Eighteen Months", monthsPerPeriod: 18 },
  { id: ScheduleType.Triennial, label: "Triennial", monthsPerPeriod: 36 }
]);
const SubscriptionScheduleComboBox = ({
  value,
  onChange,
  label = "Billing schedule",
  placeholder = "Select a schedule",
  isDisabled,
  isInvalid,
  errorMessage,
  className,
  ...rest
}) => /* @__PURE__ */ jsxs(
  ComboBox,
  {
    selectedKey: value ?? null,
    onSelectionChange: (key) => key && onChange(key),
    isDisabled,
    isInvalid,
    className,
    ...rest,
    children: [
      label && /* @__PURE__ */ jsx(Label, { children: label }),
      /* @__PURE__ */ jsxs(ComboBox.InputGroup, { children: [
        /* @__PURE__ */ jsx(Input, { variant: "secondary", placeholder }),
        /* @__PURE__ */ jsx(ComboBox.Trigger, {})
      ] }),
      isInvalid && errorMessage && /* @__PURE__ */ jsx(FieldError, { children: errorMessage }),
      /* @__PURE__ */ jsx(ComboBox.Popover, { children: /* @__PURE__ */ jsx(ListBox, { items: SUBSCRIPTION_SCHEDULES, children: (schedule) => /* @__PURE__ */ jsxs(ListBox.Item, { id: schedule.id, textValue: schedule.label, children: [
        schedule.label,
        /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
      ] }) }) })
    ]
  }
);
const SubscriptionScheduleField = FormField(
  ({
    name,
    value,
    onChange,
    showError,
    errors,
    // Uniformly's other injected props are pulled out so they are not forwarded to the combo box.
    isDirty,
    isValid,
    showValid,
    onlyDirty,
    ...rest
  }) => /* @__PURE__ */ jsx(
    SubscriptionScheduleComboBox,
    {
      name,
      id: name,
      value: value || null,
      onChange,
      isInvalid: showError,
      errorMessage: errors?.[0],
      ...rest
    }
  )
);
const SubscriptionCreateForm = reactive.component(
  ({ accountId, onSuccess, services, monitors }) => {
    const [orders, setOrders] = useState([]);
    const [form, setForm] = useState({ isValid: false, data: {} });
    const PaymentProfilesSelectField = useComponent("accounts.payment-profiles-field");
    useEffect(() => {
      if (accountId) {
        services.utilities.getEligibleOrders(Number(accountId));
      }
    }, [accountId]);
    useReactor(services.utilities.getEligibleOrders, {
      success: ({ payload }) => setOrders(payload ?? [])
    });
    useSuccessReactor(services.utilities.createSubscription, (payload, args) => {
      if (Number(args[0]?.accountId) !== Number(accountId)) return;
      appRuntime.services.alerts.success("Subscription was successfully created");
      onSuccess?.(payload);
    });
    return /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-2", onChange: setForm, children: [
      /* @__PURE__ */ jsx(
        SelectField,
        {
          name: "orderId",
          label: "Initial Order",
          type: "number",
          isLoading: monitors.getEligibleOrders.executing,
          validation: (x) => x.number().required("Initial Order is required"),
          items: orders,
          children: (order) => /* @__PURE__ */ jsxs(
            ListBox.Item,
            {
              id: order.id,
              textValue: `Order ${order.orderNumber ?? order.id}`,
              children: [
                `${parseAndFormatDate(order.completedOnUtc ?? order.enteredOnUtc)} · ${formatCurrencyUSD(
                  order.grandTotal ?? 0
                )} · ${order.statusTypeName}`,
                /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
              ]
            },
            order.id
          )
        }
      ),
      /* @__PURE__ */ jsx(
        PaymentProfilesSelectField,
        {
          accountId: Number(accountId),
          name: "paymentProfileId",
          label: "Payment Profile",
          validation: (x) => x.number().required("Payment Profile is required")
        }
      ),
      /* @__PURE__ */ jsx(
        DateField,
        {
          name: "startDate",
          label: "Start Date",
          validation: (x) => x.date().required("Start Date is required")
        }
      ),
      /* @__PURE__ */ jsx(
        SubscriptionScheduleField,
        {
          name: "schedule",
          label: "Frequency",
          validation: (x) => x.number().required("Frequency is required")
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "amount",
          label: "Amount",
          type: "string",
          startContent: "$",
          transform: "float",
          validation: (x) => x.number().required("Amount is required").positive("Amount must be > 0"),
          mask: masks.numberWithDecimalsRegex
        }
      ),
      /* @__PURE__ */ jsx(DrawerActions, { children: /* @__PURE__ */ jsx(
        SaveButton,
        {
          label: "Create Subscription",
          iconClass: "ri-add-line",
          withConfirm: true,
          isDisabled: !form.isValid,
          isLoading: monitors.createSubscription.executing,
          body: form.isValid && /* @__PURE__ */ jsxs("div", { children: [
            "This will create a new subscription for account ",
            /* @__PURE__ */ jsx("strong", { children: accountId }),
            ", billed",
            " ",
            /* @__PURE__ */ jsxs("strong", { children: [
              formatCurrencyUSD(form.data.amount ?? 0),
              " ",
              SUBSCRIPTION_SCHEDULES.find((s) => s.id === form.data.schedule)?.label,
              " "
            ] }),
            "starting ",
            /* @__PURE__ */ jsx("strong", { children: parseAndFormatDate(form.data.startDate) })
          ] }),
          onPress: () => services.utilities.createSubscription({
            accountId: Number(accountId),
            orderId: form.data.orderId,
            paymentProfileId: form.data.paymentProfileId,
            startDate: toISO(form.data.startDate),
            schedule: form.data.schedule,
            amount: form.data.amount
          })
        }
      ) })
    ] });
  },
  {
    name: "SubscriptionCreateForm",
    monitors: (services) => [
      services.utilities.getEligibleOrders,
      services.utilities.createSubscription
    ]
  }
);
const SubscriptionCreateDrawer = withDrawerController(({ item, onSuccess }) => /* @__PURE__ */ jsx(SubscriptionCreateForm, { accountId: item?.accountId, onSuccess }));
const SubscriptionResetForm = reactive.component(
  ({ subscription, onSuccess, services, monitors }) => {
    const [form, setForm] = useState({ isValid: false, data: {} });
    useEffect(() => {
      setForm({ isValid: false, data: {} });
    }, [subscription]);
    useSuccessReactor(services.utilities.resetSubscription, (payload, args) => {
      if (Number(args[0]?.subscriptionId) !== Number(subscription?.id)) return;
      appRuntime.services.alerts.success("Subscription was successfully updated");
      onSuccess?.(payload);
    });
    return subscription && /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-2", onChange: setForm, children: [
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "subscriptionId",
          label: "Subscription ID",
          value: subscription.id,
          isReadOnly: true
        }
      ),
      /* @__PURE__ */ jsx(
        SubscriptionScheduleField,
        {
          name: "schedule",
          label: "Subscription Type",
          validation: (x) => x.number().required("Subscription Type is required")
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "amount",
          label: "Amount",
          type: "string",
          startContent: "$",
          transform: "float",
          validation: (x) => x.number().required("Amount is required").positive("Amount must be > 0"),
          mask: masks.numberWithDecimalsRegex
        }
      ),
      /* @__PURE__ */ jsx(
        DateField,
        {
          name: "nextRenewal",
          label: "Next Renewal Date",
          validation: (x) => x.date().required("Next Renewal Date is required")
        }
      ),
      /* @__PURE__ */ jsx(DrawerActions, { children: /* @__PURE__ */ jsx(
        SaveButton,
        {
          label: "Reset",
          iconClass: "ri-arrow-left-right-fill",
          withConfirm: true,
          color: "danger",
          isDisabled: !form.isValid,
          isLoading: monitors.resetSubscription.executing,
          body: form.isValid && /* @__PURE__ */ jsxs("div", { children: [
            "This will disable any active adjustments (excluding payment plans) and update subscription ",
            /* @__PURE__ */ jsxs("strong", { children: [
              subscription?.id,
              " "
            ] }),
            "to be billed",
            " ",
            /* @__PURE__ */ jsxs("strong", { children: [
              formatCurrencyUSD(form.data.amount ?? 0),
              " ",
              SUBSCRIPTION_SCHEDULES.find((s) => s.id === form.data.schedule)?.label,
              " "
            ] }),
            "starting",
            " ",
            /* @__PURE__ */ jsx("strong", { children: parseAndFormatDate(form.data.nextRenewal ?? DateTime.now()) })
          ] }),
          onPress: () => services.utilities.resetSubscription({
            subscriptionId: subscription.id,
            nextRenewal: toISO(form.data.nextRenewal),
            schedule: form.data.schedule,
            amount: form.data.amount
          })
        }
      ) })
    ] });
  },
  {
    name: "SubscriptionResetForm",
    monitors: (services) => [services.utilities.resetSubscription]
  }
);
const SubscriptionSummaryTable = ({ subscription, isLoading }) => {
  const renderValue = (value) => isLoading ? /* @__PURE__ */ jsx(Skeleton, { className: "w-full h-5 rounded-lg" }) : value;
  return /* @__PURE__ */ jsx(Table, { children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Subscription details", children: [
    /* @__PURE__ */ jsxs(Table.Header, { className: "sr-only", children: [
      /* @__PURE__ */ jsx(Table.Column, { isRowHeader: true, id: "label", children: "Field" }),
      /* @__PURE__ */ jsx(Table.Column, { id: "value", children: "Value" })
    ] }),
    /* @__PURE__ */ jsxs(Table.Body, { children: [
      /* @__PURE__ */ jsxs(Table.Row, { id: "accountId", children: [
        /* @__PURE__ */ jsx(Table.Cell, { className: "text-accent", children: "Account #" }),
        /* @__PURE__ */ jsx(Table.Cell, { children: renderValue(subscription?.accountId) })
      ] }),
      /* @__PURE__ */ jsxs(Table.Row, { id: "subscriptionId", children: [
        /* @__PURE__ */ jsx(Table.Cell, { className: "text-accent", children: "Subscription ID" }),
        /* @__PURE__ */ jsx(Table.Cell, { children: renderValue(subscription?.id) })
      ] }),
      /* @__PURE__ */ jsxs(Table.Row, { id: "customer", children: [
        /* @__PURE__ */ jsx(Table.Cell, { className: "text-accent", children: "Customer" }),
        /* @__PURE__ */ jsx(Table.Cell, { children: renderValue(subscription?.accountName) })
      ] }),
      /* @__PURE__ */ jsxs(Table.Row, { id: "frequency", children: [
        /* @__PURE__ */ jsx(Table.Cell, { className: "text-accent", children: "Frequency" }),
        /* @__PURE__ */ jsx(Table.Cell, { children: renderValue(subscription?.scheduleDescription) })
      ] }),
      /* @__PURE__ */ jsxs(Table.Row, { id: "nextRenewal", children: [
        /* @__PURE__ */ jsx(Table.Cell, { className: "text-accent", children: "Next Renewal Date" }),
        /* @__PURE__ */ jsx(Table.Cell, { children: renderValue(
          subscription?.nextRenewalDateUtc && parseAndFormatDate(subscription.nextRenewalDateUtc, "utc", { tz: "utc" })
        ) })
      ] }),
      /* @__PURE__ */ jsxs(Table.Row, { id: "amount", children: [
        /* @__PURE__ */ jsx(Table.Cell, { className: "text-accent", children: "Amount" }),
        /* @__PURE__ */ jsx(Table.Cell, { children: renderValue(subscription?.amount && formatCurrencyUSD(subscription.amount)) })
      ] })
    ] })
  ] }) }) });
};
const SubscriptionResetDrawer = withDrawerController(({ item, onSuccess }) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4", children: [
  /* @__PURE__ */ jsx(SubscriptionSummaryTable, { subscription: item?.subscription }),
  /* @__PURE__ */ jsx(SubscriptionResetForm, { subscription: item?.subscription, onSuccess })
] }));
const ConfirmBody = ({ subscription, data, isPendingCancel }) => {
  const id = /* @__PURE__ */ jsx("strong", { children: subscription.id });
  switch (data.status) {
    case SubscriptionStatusType.Active:
      return /* @__PURE__ */ jsxs("div", { children: [
        "Subscription ",
        id,
        " becomes ",
        /* @__PURE__ */ jsx("strong", { children: "Active" }),
        " and renews and charges as usual again."
      ] });
    case SubscriptionStatusType.OnHold:
      return /* @__PURE__ */ jsxs("div", { children: [
        "Subscription ",
        id,
        " goes ",
        /* @__PURE__ */ jsx("strong", { children: "On Hold" }),
        ": renewals still invoice, but nothing is charged",
        " ",
        data.holdUntil ? /* @__PURE__ */ jsxs(Fragment, { children: [
          "until it returns to ",
          /* @__PURE__ */ jsx("strong", { children: SUBSCRIPTION_STATUS_LABELS[subscription.status] }),
          " ",
          "after",
          " ",
          /* @__PURE__ */ jsx("strong", { children: DateTime.fromISO(data.holdUntil, { zone: "utc" }).toLocal().toFormat("MM/dd/yyyy") }),
          "."
        ] }) : "until someone lifts the hold."
      ] });
    case SubscriptionStatusType.Canceled:
      return isPendingCancel || data.devicesReturned ? /* @__PURE__ */ jsxs("div", { children: [
        "Subscription ",
        id,
        " is ",
        /* @__PURE__ */ jsx("strong", { children: "Canceled" }),
        " now: it ends today, its original order is cancelled and the account is removed from the central station."
      ] }) : /* @__PURE__ */ jsxs("div", { children: [
        "Subscription ",
        id,
        " is ",
        /* @__PURE__ */ jsx("strong", { children: "Canceled" }),
        " now if its original order has not shipped; otherwise it waits as ",
        /* @__PURE__ */ jsx("strong", { children: "Cancellation Pending" }),
        " until the devices come back."
      ] });
    default:
      return null;
  }
};
const SubscriptionStatusForm = reactive.component(
  ({ subscription, onSuccess, services, monitors }) => {
    const [reasons, setReasons] = useState([]);
    const [form, setForm] = useState({ isValid: false, data: {} });
    const status = form.data.status;
    const isPendingCancel = subscription?.status === SubscriptionStatusType.PendingCancel;
    const chosenReason = reasonsFor(reasons, status).find((r) => r.id === form.data.reasonId);
    useEffect(() => {
      services.subscriptions.getStatusReasons();
    }, []);
    useSuccessReactor(
      services.subscriptions.getStatusReasons,
      (payload) => setReasons(payload ?? [])
    );
    useReactor(services.subscriptions.changeStatus, {
      success: ({ payload, args }) => {
        if (Number(args[0]) !== Number(subscription?.id)) return;
        appRuntime.services.alerts.success(
          `Subscription is now ${SUBSCRIPTION_STATUS_LABELS[payload?.status] ?? "updated"}.`
        );
        onSuccess?.(payload);
      },
      error: ({ payload, args }) => {
        if (Number(args[0]) !== Number(subscription?.id)) return;
        appRuntime.services.alerts.error(payload?.message ?? "Unable to change the status.");
      }
    });
    if (!subscription) return null;
    return /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-3", onChange: setForm, children: [
      /* @__PURE__ */ jsxs("p", { className: "text-muted text-sm", children: [
        "Currently ",
        /* @__PURE__ */ jsx("strong", { children: SUBSCRIPTION_STATUS_LABELS[subscription.status] }),
        subscription.statusReason ? ` — ${subscription.statusReason}` : "",
        "."
      ] }),
      /* @__PURE__ */ jsx(
        SelectField,
        {
          name: "status",
          label: "New status",
          type: "number",
          validation: (x) => x.number().required("New status is required"),
          items: statusChoices(subscription.status),
          children: (choice) => /* @__PURE__ */ jsxs(ListBox.Item, { id: choice.id, textValue: choice.label, children: [
            choice.label,
            /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
          ] }, choice.id)
        }
      ),
      needsReason(status) && /* @__PURE__ */ jsx(
        SelectField,
        {
          name: "reasonId",
          label: "Reason",
          type: "number",
          isLoading: monitors.getStatusReasons.executing,
          validation: (x) => x.number().required("Reason is required"),
          items: reasonsFor(reasons, status),
          children: (reason) => /* @__PURE__ */ jsxs(ListBox.Item, { id: reason.id, textValue: reason.reason, children: [
            reason.reason,
            /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
          ] }, reason.id)
        },
        `reason-${status}`
      ),
      chosenReason?.allowFreeText && /* @__PURE__ */ jsx(
        TextField,
        {
          name: "reason",
          label: "Describe the reason",
          validation: (x) => x.string().trim().required("Describe the reason").max(200, "The reason cannot be longer than 200 characters")
        }
      ),
      status === SubscriptionStatusType.OnHold && /* @__PURE__ */ jsx(
        DateField,
        {
          name: "holdUntil",
          label: "Hold until (leave blank to lift the hold by hand)",
          validation: (x) => x.date().nullable().transform((value, original) => original ? value : null).min(DateTime.now().startOf("day").toJSDate(), "The hold cannot end before today")
        }
      ),
      status === SubscriptionStatusType.Canceled && !isPendingCancel && /* @__PURE__ */ jsx(CheckboxField, { name: "devicesReturned", label: "The customer's devices have been returned" }),
      /* @__PURE__ */ jsx(DrawerActions, { children: /* @__PURE__ */ jsx(
        SaveButton,
        {
          label: "Change Status",
          iconClass: "ri-exchange-line",
          withConfirm: true,
          color: status === SubscriptionStatusType.Canceled ? "danger" : void 0,
          isDisabled: !form.isValid,
          isLoading: monitors.changeStatus.executing,
          body: form.isValid && /* @__PURE__ */ jsx(
            ConfirmBody,
            {
              subscription,
              data: form.data,
              isPendingCancel
            }
          ),
          onPress: () => services.subscriptions.changeStatus(
            subscription.id,
            toStatusRequest(form.data, reasons)
          )
        }
      ) })
    ] });
  },
  {
    name: "SubscriptionStatusForm",
    monitors: (services) => [
      services.subscriptions.getStatusReasons,
      services.subscriptions.changeStatus
    ]
  }
);
const SubscriptionStatusDrawer = withDrawerController(({ item, onSuccess }) => /* @__PURE__ */ jsx(SubscriptionStatusForm, { subscription: item?.subscription, onSuccess }));
const SubscriptionWidgetController = reactive.component(
  ({ services, monitors, accountId }) => {
    const [subscription, setSubscription] = useState(null);
    const [isMissing, setMissing] = useState(false);
    const [scheduledChange, setScheduledChange] = useState(null);
    const [statusHistory, setStatusHistory] = useState(null);
    const user = useUser();
    const config = useConfig();
    const apiKey = config?.api?.apiKey ?? "";
    const canReset = user.hasPolicy(apiKey, "Subscriptions", "Reset");
    const canUpdate = user.hasPolicy(apiKey, "Subscriptions", "UpdateStatus");
    const showSubscription = (saved) => {
      setSubscription(saved);
      setMissing(!saved);
    };
    const [createDrawer, CreateDrawer] = useDrawerController(SubscriptionCreateDrawer, {
      size: "md",
      // Required: withDrawerController defaults isOpen to true.
      isOpen: false,
      headerContent: () => "New Subscription",
      onSuccess: showSubscription
    });
    const [resetDrawer, ResetDrawer] = useDrawerController(SubscriptionResetDrawer, {
      size: "md",
      isOpen: false,
      headerContent: () => "Reset Subscription",
      onSuccess: showSubscription
    });
    const [statusDrawer, StatusDrawer] = useDrawerController(SubscriptionStatusDrawer, {
      size: "md",
      isOpen: false,
      headerContent: () => "Change Subscription Status",
      onSuccess: showSubscription
    });
    useReactor(services.subscriptions.findByAccount, {
      success: ({ payload, args }) => {
        if (Number(args[0]) !== Number(accountId)) return;
        showSubscription(payload);
      },
      error: ({ payload, args }) => {
        if (Number(args[0]) !== Number(accountId)) return;
        setSubscription(null);
        setMissing(false);
        appRuntime.services.alerts.error(payload?.message ?? "Unable to load the subscription.");
      }
    });
    const isOnHold = subscription?.status === SubscriptionStatusType.OnHold;
    useEffect(() => {
      setScheduledChange(null);
      if (isOnHold) services.subscriptions.findScheduledStatusChange(subscription.id);
    }, [subscription?.id, subscription?.status]);
    useSuccessReactor(services.subscriptions.findScheduledStatusChange, (payload, args) => {
      if (Number(args[0]) === Number(subscription?.id)) setScheduledChange(payload);
    });
    useEffect(() => {
      setStatusHistory(null);
      if (subscription?.id) services.subscriptions.getStatusHistory(subscription.id);
    }, [subscription?.id, subscription?.status]);
    useReactor(services.subscriptions.getStatusHistory, {
      success: ({ payload, args }) => {
        if (Number(args[0]) === Number(subscription?.id)) setStatusHistory(payload ?? []);
      },
      error: ({ payload, args }) => {
        if (Number(args[0]) !== Number(subscription?.id)) return;
        appRuntime.services.alerts.error(payload?.message ?? "Unable to load the status history.");
      }
    });
    useSuccessReactor(services.subscriptions.updateSubscription, (payload) => {
      if (Number(payload?.accountId) === Number(accountId)) setSubscription(payload);
    });
    const onSavePaymentProfile = (profileId) => services.subscriptions.updateSubscription(subscription.id, {
      ...subscription,
      paymentProfileId: profileId
    });
    const onAction = (key) => {
      if (key === SubscriptionWidgetActions.ChangeStatus) statusDrawer.open({ subscription });
      if (key === SubscriptionWidgetActions.Reset) resetDrawer.open({ subscription });
    };
    const actions = [
      canUpdate && SubscriptionWidgetActions.ChangeStatus,
      canReset && SubscriptionWidgetActions.Reset
    ].filter(Boolean);
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        SubscriptionWidget$1,
        {
          subscription,
          isLoading: monitors.findByAccount.executing,
          isMissing,
          isSaving: monitors.updateSubscription.executing,
          scheduledChange,
          statusHistory,
          isHistoryLoading: monitors.getStatusHistory.executing,
          actions,
          onAction,
          onCreate: canReset ? () => createDrawer.open({ accountId: Number(accountId) }) : void 0,
          onSavePaymentProfile
        }
      ),
      /* @__PURE__ */ jsx(CreateDrawer, {}),
      /* @__PURE__ */ jsx(ResetDrawer, {}),
      /* @__PURE__ */ jsx(StatusDrawer, {})
    ] });
  },
  {
    name: "SubscriptionWidgetController",
    sync: (services, { accountId }) => [
      {
        execute: () => accountId && services.subscriptions.findByAccount(Number(accountId)),
        deps: [accountId]
      }
    ],
    monitors: (services) => [
      services.subscriptions.findByAccount,
      services.subscriptions.updateSubscription,
      services.subscriptions.getStatusHistory
    ]
  }
);
const SubscriptionLookup = reactive.component(
  ({ onSubscriptionLoaded, services, monitors }) => {
    const [accountId, setAccountId] = useState("");
    useEffect(() => {
      if (!accountId) {
        onSubscriptionLoaded(null, null);
      }
    }, [accountId]);
    useSuccessReactor(services.subscriptions.findByAccount, (payload, args) => {
      if (Number(args[0]) === Number(accountId)) onSubscriptionLoaded(payload, Number(accountId));
    });
    return /* @__PURE__ */ jsx(Uniformly, { onChange: (form) => setAccountId(form.data.accountId), children: /* @__PURE__ */ jsxs("div", { className: "flex flex-row gap-2 items-end", children: [
      /* @__PURE__ */ jsx(TextField, { name: "accountId", label: "Account ID", className: "w-[500px]" }),
      /* @__PURE__ */ jsx(
        SaveButton,
        {
          label: "Lookup",
          iconClass: "ri-search-line",
          isDisabled: !accountId,
          isLoading: monitors.findByAccount.executing,
          onPress: () => services.subscriptions.findByAccount(Number(accountId))
        }
      )
    ] }) });
  },
  {
    name: "SubscriptionLookup",
    monitors: (services) => [services.subscriptions.findByAccount]
  }
);
const SubscriptionDetails = reactive.component(
  ({ subscription, onReset, monitors }) => /* @__PURE__ */ jsxs("div", { className: "flex gap-4", children: [
    /* @__PURE__ */ jsx("div", { className: "flex-1", children: /* @__PURE__ */ jsx(
      SubscriptionSummaryTable,
      {
        subscription,
        isLoading: monitors.findByAccount.executing
      }
    ) }),
    /* @__PURE__ */ jsx("div", { className: "flex-1", children: /* @__PURE__ */ jsx(SubscriptionResetForm, { subscription, onSuccess: onReset }) })
  ] }),
  {
    name: "SubscriptionDetails",
    monitors: (services) => [services.subscriptions.findByAccount]
  }
);
const UtilitiesView = () => {
  const [subscription, setSubscription] = useState(null);
  const [createAccountId, setCreateAccountId] = useState(null);
  const onSubscriptionLoaded = (subscription2, accountId) => {
    setSubscription(subscription2);
    setCreateAccountId(subscription2 ? null : accountId);
  };
  const onSaved = (subscription2) => {
    setCreateAccountId(null);
    setSubscription(subscription2);
  };
  return /* @__PURE__ */ jsx(Card, { className: "grow p-4", children: /* @__PURE__ */ jsxs(ScrollShadow, { hideScrollBar: true, offset: 1, className: "grow", children: [
    /* @__PURE__ */ jsx(Card.Header, { className: "flex flex-grow items-start pb-2", children: /* @__PURE__ */ jsx("p", { children: "Enter an Account # and select lookup to load the customer's subscription." }) }),
    /* @__PURE__ */ jsx(Card.Content, { children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4 lg:w-1/2", children: [
      /* @__PURE__ */ jsx(SubscriptionLookup, { onSubscriptionLoaded }),
      createAccountId && !subscription ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs("p", { className: "text-muted text-sm", children: [
          "Account ",
          createAccountId,
          " has no subscription. You can create one below."
        ] }),
        /* @__PURE__ */ jsx(
          SubscriptionCreateForm,
          {
            accountId: createAccountId,
            onSuccess: onSaved
          },
          createAccountId
        )
      ] }) : /* @__PURE__ */ jsx(SubscriptionDetails, { subscription, onReset: onSaved })
    ] }) })
  ] }) });
};
const UtilitiesRoutes = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsx(Route, { path: "/*", element: /* @__PURE__ */ jsx(UtilitiesView, {}) }) });
const Router = () => /* @__PURE__ */ jsx(AppLayout, { children: /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "subscriptions/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "subscriptions", operation: "view", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(SubscriptionsRoutes, {}) })
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "utilities/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "subscriptions", operation: "reset", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(UtilitiesRoutes, {}) })
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "subscriptions", operation: "view", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(SubscriptionsRoutes, {}) })
    }
  )
] }) });
function getSubscriptionsNavigation(runtime) {
  const user = runtime.user;
  const apiKey = runtime.config?.api?.apiKey ?? "";
  return {
    isVisible: false,
    // user.hasApiAccess(apiKey),
    items: [
      {
        title: "Manage Subscription",
        route: "/utilities",
        icon: "ri-arrow-left-right-fill",
        isVisible: user.hasPolicy(apiKey, "subscriptions", "reset")
      }
    ]
  };
}
function getSubscriptionsResources(_runtime) {
  const items = [];
  return {
    items,
    handlers: (() => ({}))
  };
}
const Embed = createEmbed({ runtime: () => appRuntime, render });
const SUBSCRIPTION_WIDGET = "subscriptions.subscription-widget";
const SubscriptionWidget = (props) => createElement(
  Embed,
  null,
  // The controller is JS, so its props are inferred rather than declared. The interface above is
  // the contract; this cast is what ties the two together.
  createElement(SubscriptionWidgetController, props)
);
let appRuntime = null;
const initSubscriptionsRuntime = (hostRuntime) => {
  appRuntime = hostRuntime.registerApp("subscriptions", {
    description: {
      title: "Subscriptions",
      iconClass: "ri-refresh-fill",
      defaultRoute: "/subscriptions",
      isVisible: (runtime) => runtime.user.hasApiAccess(runtime.config?.api?.apiKey ?? "")
    },
    routes: Router,
    navigation: getSubscriptionsNavigation,
    resources: getSubscriptionsResources,
    config: () => SubscriptionsConfig.current
  });
  hostRuntime.components.register(SUBSCRIPTION_WIDGET, SubscriptionWidget, {
    slot: "accounts.customer-overview",
    order: 30
  });
};
export {
  initSubscriptionsRuntime
};
//# sourceMappingURL=index.js.map
